<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import Avatar from 'next/avatar/Avatar.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import ChatActionTooltip from 'widget/components/ChatActionTooltip.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const PREVIEW_WAVEFORM_HEIGHT = 28;
const PREVIEW_WAVEFORM_BAR_WIDTH = 2;
const PREVIEW_WAVEFORM_BAR_GAP = 1;
const PREVIEW_WAVEFORM_SAMPLES = 51;

const buildPreviewWaveform = elapsedSeconds =>
  Array.from({ length: PREVIEW_WAVEFORM_SAMPLES }, (_, index) => {
    const amplitude =
      Math.sin(index * 1.7 + elapsedSeconds) *
      Math.cos(index * 0.43 + elapsedSeconds * 0.7);
    return Math.max(
      2,
      Math.min(PREVIEW_WAVEFORM_HEIGHT - 2, Math.abs(amplitude) * 24)
    );
  });

const { t } = useI18n();

const isInputFocused = ref(false);
const previewInput = ref('');
const recorderState = ref('idle');
const elapsedSeconds = ref(0);
const isPreviewPlaying = ref(false);
const waveformBars = ref(buildPreviewWaveform(0));
let recordingInterval = null;
let processingTimeout = null;

const actionLabels = computed(() => ({
  ATTACH_FILE: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.ATTACH_FILE'),
  RECORD_AUDIO: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.RECORD_AUDIO'),
  INSERT_EMOJI: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.INSERT_EMOJI'),
  SEND_MESSAGE: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.SEND_MESSAGE'),
  PAUSE_RECORDING: t(
    'INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.PAUSE_RECORDING'
  ),
  RESUME_RECORDING: t(
    'INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.RESUME_RECORDING'
  ),
  STOP_RECORDING: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.STOP_RECORDING'),
  CANCEL_RECORDING: t(
    'INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.CANCEL_RECORDING'
  ),
  PLAY_RECORDING: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.PLAY_RECORDING'),
  PAUSE_PLAYBACK: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.PAUSE_PLAYBACK'),
  DISCARD_RECORDING: t(
    'INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.DISCARD_RECORDING'
  ),
  PROCESSING: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.ACTIONS.PROCESSING'),
}));

const footerTranslation = key => actionLabels.value[key];

const formatTime = value => {
  const minutes = Math.floor(value / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(value % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const elapsedTime = computed(() => formatTime(elapsedSeconds.value));
const playbackTime = computed(() => `${formatTime(0)} / ${elapsedTime.value}`);
const isRecording = computed(() => recorderState.value === 'recording');
const isPaused = computed(() => recorderState.value === 'paused');
const isProcessing = computed(() => recorderState.value === 'processing');
const hasAudioPreview = computed(() => recorderState.value === 'ready');
const hasSendableContent = computed(
  () => previewInput.value.trim().length > 0 || hasAudioPreview.value
);
const isSendDisabled = computed(
  () =>
    isRecording.value ||
    isPaused.value ||
    isProcessing.value ||
    !hasSendableContent.value
);

const stopRecordingTimer = () => {
  window.clearInterval(recordingInterval);
  recordingInterval = null;
};

const startRecordingTimer = () => {
  stopRecordingTimer();
  recordingInterval = window.setInterval(() => {
    elapsedSeconds.value += 1;
    waveformBars.value = buildPreviewWaveform(elapsedSeconds.value);
  }, 1000);
};

const startPreviewRecording = () => {
  window.clearTimeout(processingTimeout);
  elapsedSeconds.value = 0;
  waveformBars.value = buildPreviewWaveform(0);
  recorderState.value = 'recording';
  isPreviewPlaying.value = false;
  startRecordingTimer();
};

const toggleRecordingPause = () => {
  if (isRecording.value) {
    recorderState.value = 'paused';
    stopRecordingTimer();
    return;
  }

  if (isPaused.value) {
    recorderState.value = 'recording';
    startRecordingTimer();
  }
};

const stopPreviewRecording = () => {
  if (!isRecording.value && !isPaused.value) return;

  stopRecordingTimer();
  elapsedSeconds.value = Math.max(elapsedSeconds.value, 1);
  recorderState.value = 'processing';
  processingTimeout = window.setTimeout(() => {
    recorderState.value = 'ready';
  }, 600);
};

const resetPreviewRecording = () => {
  stopRecordingTimer();
  window.clearTimeout(processingTimeout);
  elapsedSeconds.value = 0;
  isPreviewPlaying.value = false;
  recorderState.value = 'idle';
};

const sendPreviewMessage = () => {
  if (isSendDisabled.value) return;
  previewInput.value = '';
  resetPreviewRecording();
};

onBeforeUnmount(() => {
  stopRecordingTimer();
  window.clearTimeout(processingTimeout);
});

const getStatusText = computed(() => {
  return props.config.isOnline
    ? t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.ONLINE')
    : t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.OFFLINE');
});

const shouldShowConversationChoices = computed(
  () =>
    props.config.isDefaultScreen &&
    (props.config.hasSupportRequestForm || props.config.hasAgentConversation)
);

const helpdeskDescription = computed(() => {
  if (
    props.config.hasSupportRequestForm &&
    !props.config.hasAgentConversation
  ) {
    return t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.SUPPORT_ONLY_DESCRIPTION');
  }

  if (
    !props.config.hasSupportRequestForm &&
    props.config.hasAgentConversation
  ) {
    return t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.AGENT_ONLY_DESCRIPTION');
  }

  return t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.HELPDESK_DESCRIPTION');
});
</script>

<template>
  <div class="relative flex flex-col w-full px-4">
    <div
      v-if="config.isDefaultScreen"
      class="flex flex-col gap-3 rounded-md p-4 shadow-sm bg-n-background dark:bg-n-solid-2"
    >
      <div class="flex items-center justify-between">
        <div>
          <div
            class="text-sm font-medium leading-4 text-n-slate-12 dark:text-n-slate-50"
          >
            {{ getStatusText }}
          </div>
          <div class="mt-1 text-xs text-n-slate-11">
            {{ config.replyTime }}
          </div>
        </div>
        <Avatar name="C" :size="34" rounded-full />
      </div>
      <div v-if="shouldShowConversationChoices" class="flex flex-col gap-2">
        <div class="flex flex-col gap-0.5">
          <h3 class="text-xs font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.HELPDESK_TITLE') }}
          </h3>
          <p class="text-xxs text-n-slate-11">
            {{ helpdeskDescription }}
          </p>
        </div>
        <button
          v-if="config.hasSupportRequestForm"
          class="flex w-full items-center justify-between gap-2 rounded-md bg-[var(--widget-preview-color)] px-3 py-2 text-left text-xs font-medium text-white dark:text-white"
        >
          <span class="flex flex-col gap-0.5">
            <span>
              {{ $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.SUPPORT_REQUEST_TITLE') }}
            </span>
            <span class="text-xxs font-normal text-white/80">
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.FOOTER.SUPPORT_REQUEST_DESCRIPTION'
                )
              }}
            </span>
          </span>
          <FluentIcon icon="arrow-right" size="14" />
        </button>
        <button
          v-if="config.hasAgentConversation"
          class="flex w-full items-center justify-between gap-2 rounded-md bg-n-alpha-2 px-3 py-2 text-left text-xs font-medium text-n-slate-12"
        >
          <span class="flex flex-col gap-0.5">
            <span>
              {{
                $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.AGENT_CONVERSATION_TITLE')
              }}
            </span>
            <span class="text-xxs font-normal text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.FOOTER.AGENT_CONVERSATION_DESCRIPTION'
                )
              }}
            </span>
          </span>
          <i class="i-ph-chat-circle shrink-0 text-sm" />
        </button>
      </div>
      <button
        v-else-if="config.isDefaultScreen"
        class="inline-flex items-center justify-between px-2 py-1 mt-1 -ml-2 font-medium leading-6 bg-transparent rounded-md text-[var(--widget-preview-color)] dark:bg-transparent"
      >
        <span class="pr-2 text-xs">
          {{
            $t(
              'INBOX_MGMT.WIDGET_BUILDER.FOOTER.START_CONVERSATION_BUTTON_TEXT'
            )
          }}
        </span>
        <FluentIcon icon="arrow-right" size="14" />
      </button>
    </div>
    <div v-else>
      <div
        class="flex flex-col rounded-[7px] bg-n-background transition-all duration-200 !shadow-[0_0_0_1px] dark:bg-n-slate-3"
        :class="{
          '!shadow-[var(--widget-preview-color)]': isInputFocused,
          '!shadow-n-strong': !isInputFocused,
        }"
      >
        <div class="px-3 pt-2">
          <ResizableTextArea
            id="chat-input"
            v-model="previewInput"
            :rows="1"
            :placeholder="
              $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.CHAT_INPUT_PLACEHOLDER')
            "
            class="reset-base mb-1 h-8 max-h-20 min-h-8 w-full resize-none !border-0 bg-n-background px-0 py-1 text-sm text-n-slate-12 !outline-none !ring-0 !shadow-none placeholder:text-n-slate-10 focus:!border-0 focus:!outline-none focus:!ring-0 focus:!shadow-none dark:bg-n-slate-3"
            @focus="isInputFocused = true"
            @blur="isInputFocused = false"
          />
        </div>

        <div
          class="flex min-h-11 items-center gap-1 border-t border-n-weak px-2 py-1"
        >
          <div
            v-if="hasAudioPreview"
            class="flex min-w-0 flex-1 items-center gap-1"
          >
            <ChatActionTooltip
              align="start"
              :label="
                footerTranslation(
                  isPreviewPlaying ? 'PAUSE_PLAYBACK' : 'PLAY_RECORDING'
                )
              "
            >
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-[var(--widget-preview-color)] hover:bg-n-alpha-2"
                :aria-label="
                  footerTranslation(
                    isPreviewPlaying ? 'PAUSE_PLAYBACK' : 'PLAY_RECORDING'
                  )
                "
                @click="isPreviewPlaying = !isPreviewPlaying"
              >
                <i
                  :class="
                    isPreviewPlaying ? 'i-ph-pause-fill' : 'i-ph-play-fill'
                  "
                  class="text-base"
                />
              </button>
            </ChatActionTooltip>

            <span class="shrink-0 text-[10px] tabular-nums text-n-slate-11">
              {{ playbackTime }}
            </span>
            <div class="h-1 min-w-6 flex-1 rounded-full bg-n-alpha-2">
              <div
                class="h-1 w-1/3 rounded-full bg-[var(--widget-preview-color)]"
              />
            </div>

            <ChatActionTooltip
              align="end"
              :label="footerTranslation('DISCARD_RECORDING')"
            >
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-ruby-9"
                :aria-label="footerTranslation('DISCARD_RECORDING')"
                @click="resetPreviewRecording"
              >
                <i class="i-ph-trash text-base" />
              </button>
            </ChatActionTooltip>
          </div>

          <div
            v-else-if="isRecording || isPaused"
            class="flex min-w-0 flex-1 items-center gap-1"
          >
            <span
              class="size-2 shrink-0 rounded-full"
              :class="isPaused ? 'bg-n-amber-9' : 'animate-pulse bg-n-ruby-9'"
            />
            <svg
              viewBox="0 0 152 28"
              preserveAspectRatio="none"
              class="h-7 min-w-6 flex-1 text-[var(--widget-preview-color)]"
              aria-hidden="true"
            >
              <rect
                v-for="(barHeight, index) in waveformBars"
                :key="index"
                :x="
                  index *
                  (PREVIEW_WAVEFORM_BAR_WIDTH + PREVIEW_WAVEFORM_BAR_GAP)
                "
                :y="(PREVIEW_WAVEFORM_HEIGHT - barHeight) / 2"
                :width="PREVIEW_WAVEFORM_BAR_WIDTH"
                :height="barHeight"
                :rx="PREVIEW_WAVEFORM_BAR_WIDTH / 2"
                fill="currentColor"
              />
            </svg>
            <span class="shrink-0 text-[10px] tabular-nums text-n-slate-11">
              {{ elapsedTime }}
            </span>

            <ChatActionTooltip
              :label="
                footerTranslation(
                  isPaused ? 'RESUME_RECORDING' : 'PAUSE_RECORDING'
                )
              "
            >
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
                :aria-label="
                  footerTranslation(
                    isPaused ? 'RESUME_RECORDING' : 'PAUSE_RECORDING'
                  )
                "
                @click="toggleRecordingPause"
              >
                <i
                  :class="isPaused ? 'i-ph-play-fill' : 'i-ph-pause-fill'"
                  class="text-base"
                />
              </button>
            </ChatActionTooltip>

            <ChatActionTooltip :label="footerTranslation('STOP_RECORDING')">
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
                :aria-label="footerTranslation('STOP_RECORDING')"
                @click="stopPreviewRecording"
              >
                <i class="i-ph-stop-fill text-base" />
              </button>
            </ChatActionTooltip>

            <ChatActionTooltip
              align="end"
              :label="footerTranslation('CANCEL_RECORDING')"
            >
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-ruby-9"
                :aria-label="footerTranslation('CANCEL_RECORDING')"
                @click="resetPreviewRecording"
              >
                <i class="i-ph-x text-base" />
              </button>
            </ChatActionTooltip>
          </div>

          <div
            v-else-if="isProcessing"
            class="flex min-w-0 flex-1 items-center gap-2 px-2 text-xs text-n-slate-11"
          >
            <i class="i-ph-circle-notch animate-spin text-base" />
            <span class="truncate">
              {{ footerTranslation('PROCESSING') }}
            </span>
          </div>

          <div v-else class="flex min-w-0 flex-1 items-center gap-1">
            <ChatActionTooltip
              v-if="config.hasAttachments"
              align="start"
              :label="footerTranslation('ATTACH_FILE')"
            >
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
                :aria-label="footerTranslation('ATTACH_FILE')"
              >
                <FluentIcon icon="attach" />
              </button>
            </ChatActionTooltip>

            <ChatActionTooltip
              v-if="config.hasAttachments"
              :label="footerTranslation('RECORD_AUDIO')"
            >
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
                :aria-label="footerTranslation('RECORD_AUDIO')"
                @click="startPreviewRecording"
              >
                <i class="i-ph-microphone text-base" />
              </button>
            </ChatActionTooltip>

            <ChatActionTooltip
              v-if="config.hasEmojiPicker"
              :label="footerTranslation('INSERT_EMOJI')"
            >
              <button
                type="button"
                class="flex size-8 cursor-pointer items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
                :aria-label="footerTranslation('INSERT_EMOJI')"
              >
                <FluentIcon icon="emoji" />
              </button>
            </ChatActionTooltip>
          </div>

          <ChatActionTooltip
            align="end"
            :label="footerTranslation('SEND_MESSAGE')"
          >
            <button
              type="button"
              :disabled="isSendDisabled"
              class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-[var(--widget-preview-color)] hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-40"
              :aria-label="footerTranslation('SEND_MESSAGE')"
              @click="sendPreviewMessage"
            >
              <i
                v-if="isProcessing"
                class="i-ph-circle-notch animate-spin text-base"
              />
              <FluentIcon v-else icon="send" />
            </button>
          </ChatActionTooltip>
        </div>
      </div>
    </div>
  </div>
</template>
